/**
 * One visit type's other names. Roger's are read-only; the agency's own can
 * be renamed or removed, and new ones added. Each change saves at once.
 */

import React, { useState } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
} from '@roger/ui-components';
import { Check, Pencil, X } from 'lucide-react';
import type { TaskTypeAliasResponse } from '../../../services/api/types.gen';
import type { VisitTypesData, VisitTypesRow } from './useVisitTypesData';

const AliasItem: React.FC<{
  alias: TaskTypeAliasResponse;
  data: VisitTypesData;
  canEdit: boolean;
  onRemove: (alias: TaskTypeAliasResponse) => void;
}> = ({ alias, data, canEdit, onRemove }) => {
  const [draft, setDraft] = useState<string | null>(null);
  if (draft !== null) {
    const save = () => {
      const name = draft.trim();
      if (!name || name === alias.name) return setDraft(null);
      void data.renameAlias(alias.id, name).then(saved => {
        if (saved) setDraft(null);
      });
    };
    return (
      <li className='flex items-center gap-2'>
        <Input
          inputSize='sm'
          autoFocus
          value={draft}
          aria-label={`Rename ${alias.name}`}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') save();
            if (e.key === 'Escape') {
              e.stopPropagation();
              setDraft(null);
            }
          }}
        />
        <Button
          size='sm'
          variant='ghost'
          aria-label='Save'
          disabled={data.aliasBusy}
          onClick={save}
        >
          <Check className='h-4 w-4' />
        </Button>
      </li>
    );
  }
  return (
    <li className='flex h-8 items-center gap-2 text-sm'>
      <span className='flex-1 truncate'>{alias.name}</span>
      {alias.is_roger ? (
        <span className='text-xs text-muted-foreground'>Roger</span>
      ) : (
        canEdit && (
          <>
            {alias.can_rename && (
              <Button
                size='sm'
                variant='ghost'
                aria-label={`Rename ${alias.name}`}
                disabled={data.aliasBusy}
                onClick={() => setDraft(alias.name)}
              >
                <Pencil className='h-3.5 w-3.5' />
              </Button>
            )}
            <Button
              size='sm'
              variant='ghost'
              aria-label={`Remove ${alias.name}`}
              disabled={data.aliasBusy}
              onClick={() => onRemove(alias)}
            >
              <X className='h-3.5 w-3.5' />
            </Button>
          </>
        )
      )}
    </li>
  );
};

export const AliasDialog: React.FC<{
  /** The row being edited, or null when closed. */
  row: VisitTypesRow | null;
  data: VisitTypesData;
  canEdit: boolean;
  onClose: () => void;
}> = ({ row, data, canEdit, onClose }) => {
  const [name, setName] = useState('');
  const [toRemove, setToRemove] = useState<TaskTypeAliasResponse | null>(null);
  // Read from the latest rows, so each save shows straight away.
  const current = row ? data.rows.find(r => r.id === row.id) : undefined;
  const aliases = current?.taskTypeRow?.aliases ?? [];

  const add = () => {
    if (!current || !name.trim()) return;
    void data.addAlias(current, name.trim()).then(saved => {
      if (saved) setName('');
    });
  };

  return (
    <>
      <Dialog
        open={!!row}
        onOpenChange={open => {
          if (!open) {
            setName('');
            onClose();
          }
        }}
      >
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Aliases for {row?.name}</DialogTitle>
          </DialogHeader>
          {aliases.length > 0 ? (
            <ul className='space-y-1'>
              {aliases.map(alias => (
                <AliasItem
                  key={alias.id}
                  alias={alias}
                  data={data}
                  canEdit={canEdit}
                  onRemove={setToRemove}
                />
              ))}
            </ul>
          ) : (
            <p className='text-sm text-muted-foreground'>No aliases yet.</p>
          )}
          {canEdit && (
            <div className='flex items-center gap-2'>
              <Input
                inputSize='sm'
                placeholder='Add an alias'
                value={name}
                onChange={e => setName(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') add();
                }}
              />
              <Button
                size='sm'
                onClick={add}
                disabled={!name.trim() || data.aliasBusy}
              >
                Add
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={toRemove !== null}
        title='Remove alias?'
        message={`"${toRemove?.name ?? ''}" will no longer be offered for new tasks. Tasks already scheduled under it keep the name.`}
        confirmLabel='Remove'
        cancelLabel='Cancel'
        onAccept={() => {
          if (toRemove) void data.archiveAlias(toRemove.id);
          setToRemove(null);
        }}
        onCancel={() => setToRemove(null)}
      />
    </>
  );
};
